import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Avatar, Button, Card, Col, Divider, Empty, Input, InputNumber, List, Row,
    Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    SearchOutlined, PlusOutlined, DeleteOutlined, SaveOutlined,
    CheckCircleFilled, WarningFilled, ArrowLeftOutlined, FileTextOutlined,
    AuditOutlined, UserOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;

const PRIMARY = "#242166";
const SUCCESS = "#16a34a";
const DANGER = "#dc2626";

const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;
const dossierNum = (d) => d?.numSinistre || d?.numeroSinistreM || d?.numeroSinistreP || "—";
// N° de sinistre de la COMPAGNIE (ancien système) : affiché sous le n° PoolIns pour que les
// utilisateurs encore sur leur ancien référentiel s'y retrouvent. Masqué s'il est absent ou
// identique au n° déjà affiché.
const dossierCie = (d) => {
    const m = (d?.numeroSinistreM || "").trim();
    return m && m !== dossierNum(d) ? m : "";
};
const milThousand = (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const milParse = (v) => `${v}`.replace(/\s/g, "");

/**
 * Règlement multiple des entités de constat — v2. Workflow modernisé (AntD v5) :
 * 1) choisir l'entité + responsable obligatoire ; 2) ajouter ses dossiers ;
 * 3) saisir frais de constat/accessoires (contrôle SAP) ; 4) enregistrer.
 * Mêmes endpoints que la v1 : /api/entite-constat, /api/sinistre/entite-constat/{id},
 * POST /api/decaissement/all-entites-constat (payload : codeId, fraisConstat,
 * fraisAccessoires, entiteConstatId, responsable).
 */
const GestionPaiementSinistreEntitesConstatV2 = () => {
    const navigate = useNavigate();

    const [entites, setEntites] = useState([]);
    const [entite, setEntite] = useState(null);
    const [responsable, setResponsable] = useState("");
    const [responsableTouched, setResponsableTouched] = useState(false);

    const [sinistres, setSinistres] = useState([]);
    const [loadingDossiers, setLoadingDossiers] = useState(false);
    const [searchDossier, setSearchDossier] = useState("");

    const [selection, setSelection] = useState([]);
    const [saving, setSaving] = useState(false);

    // ── Chargement des entités ────────────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get("/api/entite-constat")
            .then((res) => setEntites(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des entités de constat."); });
    }, []);

    // ── Sélection d'une entité → chargement de ses dossiers ───────────────────
    const handleSelectEntite = (entiteConstatId) => {
        const ent = entites.find((x) => x.entiteConstatId === entiteConstatId) || null;
        setEntite(ent);
        setSelection([]);
        setSearchDossier("");
        setResponsable("");
        setResponsableTouched(false);
        if (!ent) { setSinistres([]); return; }

        setLoadingDossiers(true);
        axiosInstance.get(`/api/sinistre/entite-constat/${ent.entiteConstatId}`)
            .then((res) => {
                const data = Array.isArray(res.data)
                    ? res.data
                    : res.data?.content ?? res.data?.data ?? res.data?.items ?? [];
                setSinistres(data);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des dossiers."); })
            .finally(() => setLoadingDossiers(false));
    };

    // ── Panier ────────────────────────────────────────────────────────────────
    const ajouter = (dossier) => {
        if (selection.find((d) => d.codeId === dossier.codeId)) return;
        setSelection((prev) => [...prev, { ...dossier, fraisConstat: 0, fraisAccessoires: 0 }]);
    };
    const retirer = (codeId) => setSelection((prev) => prev.filter((d) => d.codeId !== codeId));
    const setMontant = (codeId, champ, valeur) =>
        setSelection((prev) => prev.map((d) => d.codeId === codeId ? { ...d, [champ]: Number(valeur) || 0 } : d));

    const estValide = (d) => (Number(d.fraisConstat || 0) + Number(d.fraisAccessoires || 0)) <= Number(d.sap ?? Infinity);

    const dossiersDisponibles = useMemo(() => {
        const q = searchDossier.trim().toLowerCase();
        return sinistres.filter((d) =>
            !selection.find((s) => s.codeId === d.codeId) &&
            (!q || dossierNum(d).toLowerCase().includes(q) || dossierCie(d).toLowerCase().includes(q))
        );
    }, [sinistres, selection, searchDossier]);

    const totaux = useMemo(() => {
        const totalFraisConstat = selection.reduce((a, d) => a + Number(d.fraisConstat || 0), 0);
        const totalFraisAccessoires = selection.reduce((a, d) => a + Number(d.fraisAccessoires || 0), 0);
        const totalBrut = totalFraisConstat + totalFraisAccessoires;
        return { totalFraisConstat, totalFraisAccessoires, totalBrut };
    }, [selection]);

    const erreurs = selection.filter((d) => !estValide(d)).length;
    const responsableOk = !!responsable.trim();
    const peutEnregistrer = selection.length > 0 && erreurs === 0 && totaux.totalBrut > 0 && responsableOk;

    const enregistrer = () => {
        if (!responsableOk) { setResponsableTouched(true); toast.warning("Renseignez le premier responsable / commissaire principal."); return; }
        if (!peutEnregistrer || !entite) return;
        setSaving(true);
        const payload = selection.map((d) => ({
            codeId: d.codeId,
            fraisConstat: Number(d.fraisConstat || 0),
            fraisAccessoires: Number(d.fraisAccessoires || 0),
            entiteConstatId: entite.entiteConstatId,
            responsable: responsable.trim(),
        }));
        axiosInstance.post("/api/decaissement/all-entites-constat", JSON.stringify(payload), {
            headers: { "Content-Type": "application/json" },
        })
            .then(() => {
                toast.success("Règlement enregistré avec succès.");
                setSelection([]);
                setEntite(null);
                setSinistres([]);
                setResponsable("");
                setResponsableTouched(false);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'enregistrement du règlement."); })
            .finally(() => setSaving(false));
    };

    const entiteOptions = useMemo(() => entites.map((e) => ({
        value: e.entiteConstatId,
        label: e.nom,
        adresse: e.adresse,
    })), [entites]);

    // ── Colonnes du tableau « Dossiers à régler » ─────────────────────────────
    const columns = [
        {
            title: "Dossier", key: "dossier", width: 190,
            render: (_, d) => (
                <div>
                    <Text strong style={{ fontFamily: "monospace" }}>{dossierNum(d)}</Text>
                    {dossierCie(d) && (
                        <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
                            N°&nbsp;cie&nbsp;: {dossierCie(d)}
                        </div>
                    )}
                    {d.sap != null && <div style={{ fontSize: 12, color: "#9ca3af" }}>SAP&nbsp;: {fcfa(d.sap)}</div>}
                </div>
            ),
        },
        {
            title: "Frais de constat", key: "fraisConstat", width: 150,
            render: (_, d) => (
                <InputNumber
                    style={{ width: "100%" }} min={0} value={d.fraisConstat || 0}
                    controls={false} formatter={milThousand} parser={milParse}
                    onChange={(v) => setMontant(d.codeId, "fraisConstat", v)}
                />
            ),
        },
        {
            title: "Frais accessoires", key: "frais", width: 150,
            render: (_, d) => (
                <InputNumber
                    style={{ width: "100%" }} min={0} value={d.fraisAccessoires || 0}
                    controls={false} formatter={milThousand} parser={milParse}
                    onChange={(v) => setMontant(d.codeId, "fraisAccessoires", v)}
                />
            ),
        },
        {
            title: "Total", key: "total", width: 150, align: "right",
            render: (_, d) => {
                const total = Number(d.fraisConstat || 0) + Number(d.fraisAccessoires || 0);
                const ok = estValide(d);
                return (
                    <div>
                        <Text strong style={{ color: ok ? PRIMARY : DANGER }}>{fcfa(total)}</Text>
                        <div>
                            {ok
                                ? <Tag color="success" icon={<CheckCircleFilled />} style={{ marginInlineEnd: 0 }}>OK</Tag>
                                : <Tag color="error" icon={<WarningFilled />} style={{ marginInlineEnd: 0 }}>Dépasse le SAP</Tag>}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "", key: "action", width: 44, align: "center",
            render: (_, d) => (
                <Tooltip title="Retirer">
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => retirer(d.codeId)} />
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── En-tête ── */}
                            <div style={{
                                background: `linear-gradient(120deg, ${PRIMARY} 0%, #3b378f 100%)`,
                                borderRadius: 16, padding: "22px 26px", marginBottom: 18,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                flexWrap: "wrap", gap: 12, boxShadow: "0 6px 18px rgba(36,33,102,0.22)",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                    <div style={{
                                        width: 50, height: 50, borderRadius: 12, background: "rgba(255,255,255,0.15)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <AuditOutlined style={{ fontSize: 26, color: "#fff" }} />
                                    </div>
                                    <div>
                                        <Title level={3} style={{ color: "#fff", margin: 0 }}>Règlement des entités de constat</Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Règlement cumulé des frais de constat
                                        </Text>
                                    </div>
                                </div>
                                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ borderRadius: 8 }}>
                                    Retour
                                </Button>
                            </div>

                            <Row gutter={[18, 18]}>
                                {/* ── Colonne gauche : sélection ── */}
                                <Col xs={24} lg={9}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={<Space><AuditOutlined style={{ color: PRIMARY }} /><span>1. Entité &amp; responsable</span></Space>}
                                    >
                                        <Text type="secondary" style={{ fontSize: 13 }}>Entité de constat</Text>
                                        <Select
                                            showSearch allowClear style={{ width: "100%", marginTop: 6 }}
                                            placeholder="Rechercher une entité…" optionFilterProp="label"
                                            value={entite?.entiteConstatId} onChange={(v) => handleSelectEntite(v)}
                                            options={entiteOptions}
                                            optionRender={(opt) => (
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
                                                    {opt.data.adresse && <div style={{ fontSize: 12, color: "#9ca3af" }}>{opt.data.adresse}</div>}
                                                </div>
                                            )}
                                        />

                                        {entite && (
                                            <>
                                                <div style={{
                                                    marginTop: 14, padding: 12, borderRadius: 10, background: "#f5f5ff",
                                                    display: "flex", alignItems: "center", gap: 12,
                                                }}>
                                                    <Avatar size={42} style={{ background: PRIMARY }} icon={<AuditOutlined />} />
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: PRIMARY }}>{entite.nom}</div>
                                                        {entite.adresse && <div style={{ fontSize: 12, color: "#6b7280" }}>{entite.adresse}</div>}
                                                    </div>
                                                </div>

                                                <div style={{ marginTop: 14 }}>
                                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                                        <UserOutlined /> Premier responsable / Commissaire principal <span style={{ color: DANGER }}>*</span>
                                                    </Text>
                                                    <Input
                                                        style={{ marginTop: 6 }}
                                                        placeholder="Ex : Commissaire AGBEKO Koffi"
                                                        value={responsable}
                                                        status={responsableTouched && !responsableOk ? "error" : ""}
                                                        onChange={(e) => setResponsable(e.target.value)}
                                                        onBlur={() => setResponsableTouched(true)}
                                                    />
                                                    {responsableTouched && !responsableOk && (
                                                        <Text type="danger" style={{ fontSize: 12 }}>
                                                            <WarningFilled /> Champ obligatoire pour enregistrer.
                                                        </Text>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </Card>

                                    {/* Dossiers disponibles */}
                                    <Card
                                        bordered={false}
                                        style={{ borderRadius: 14, marginTop: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <FileTextOutlined style={{ color: PRIMARY }} />
                                                <span>2. Dossiers disponibles</span>
                                                {entite && <Tag color="default">{dossiersDisponibles.length}</Tag>}
                                            </Space>
                                        }
                                    >
                                        {!entite ? (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionnez d'abord une entité" />
                                        ) : (
                                            <>
                                                <Input
                                                    allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                                    placeholder="Rechercher un dossier…" value={searchDossier}
                                                    onChange={(e) => setSearchDossier(e.target.value)} style={{ marginBottom: 12 }}
                                                />
                                                <List
                                                    loading={loadingDossiers}
                                                    dataSource={dossiersDisponibles}
                                                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={sinistres.length === 0 ? "Aucun dossier pour cette entité" : "Tous les dossiers ont été ajoutés"} /> }}
                                                    style={{ maxHeight: 360, overflowY: "auto" }}
                                                    renderItem={(d) => (
                                                        <List.Item
                                                            style={{ padding: "10px 4px" }}
                                                            actions={[
                                                                <Button key="add" type="primary" ghost size="small" icon={<PlusOutlined />} onClick={() => ajouter(d)}>
                                                                    Ajouter
                                                                </Button>,
                                                            ]}
                                                        >
                                                            <List.Item.Meta
                                                                title={
                                                                    <span style={{ fontFamily: "monospace" }}>
                                                                        {dossierNum(d)}
                                                                        {dossierCie(d) && (
                                                                            <span style={{ display: "block", fontSize: 11, color: "#9ca3af", fontWeight: 400, marginTop: 2 }}>
                                                                                N°&nbsp;cie&nbsp;: {dossierCie(d)}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                }
                                                                description={<span style={{ fontSize: 12 }}>{d.type ? `${d.type} · ` : ""}{d.sap != null ? `SAP : ${fcfa(d.sap)}` : ""}</span>}
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            </>
                                        )}
                                    </Card>
                                </Col>

                                {/* ── Colonne droite : panier + récap ── */}
                                <Col xs={24} lg={15}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <SaveOutlined style={{ color: PRIMARY }} />
                                                <span>3. Dossiers à régler</span>
                                                <Tag color={selection.length ? "processing" : "default"}>{selection.length}</Tag>
                                                {erreurs > 0 && <Tag color="error">{erreurs} en erreur</Tag>}
                                            </Space>
                                        }
                                    >
                                        <Table
                                            rowKey="codeId" size="middle" columns={columns} dataSource={selection}
                                            pagination={false} scroll={{ x: "max-content" }}
                                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ajoutez des dossiers depuis la liste de gauche" /> }}
                                        />

                                        {selection.length > 0 && (
                                            <>
                                                <Divider style={{ margin: "18px 0" }} />

                                                {/* Rappel responsable */}
                                                <div style={{
                                                    display: "flex", alignItems: "center", gap: 8, marginBottom: 14,
                                                    padding: "10px 14px", borderRadius: 10, fontSize: 13,
                                                    background: responsableOk ? "#f0fbf5" : "#fff8f8",
                                                    border: `1px solid ${responsableOk ? "#a8e6cf" : "#f5c6cb"}`,
                                                    color: responsableOk ? "#145A32" : DANGER,
                                                }}>
                                                    <UserOutlined />
                                                    {responsableOk
                                                        ? <span><strong>Responsable&nbsp;:</strong> {responsable.trim()}</span>
                                                        : <span>Renseignez le responsable de l'entité (colonne de gauche) pour pouvoir enregistrer.</span>}
                                                </div>

                                                <Row gutter={[16, 16]} align="middle">
                                                    <Col xs={24} md={16}>
                                                        <div style={{
                                                            background: "#f8fafc", borderRadius: 12, padding: "16px 18px",
                                                            border: "1px solid #eef2f7",
                                                        }}>
                                                            <Row gutter={[16, 12]}>
                                                                <Col span={12}><Statistic title="Total frais de constat" value={totaux.totalFraisConstat} suffix="FCFA" valueStyle={{ fontSize: 16 }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title="Total frais accessoires" value={totaux.totalFraisAccessoires} suffix="FCFA" valueStyle={{ fontSize: 16 }} groupSeparator=" " /></Col>
                                                            </Row>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} md={8}>
                                                        <div style={{
                                                            background: `linear-gradient(135deg, ${SUCCESS} 0%, #15803d 100%)`,
                                                            borderRadius: 12, padding: "18px 20px", color: "#fff", textAlign: "center",
                                                        }}>
                                                            <div style={{ fontSize: 13, opacity: 0.9 }}>Montant total</div>
                                                            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{fcfa(totaux.totalBrut)}</div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div style={{ marginTop: 18, textAlign: "right" }}>
                                                    <Button
                                                        type="primary" size="large" icon={<SaveOutlined />}
                                                        loading={saving} disabled={!peutEnregistrer} onClick={enregistrer}
                                                        style={{ background: peutEnregistrer ? SUCCESS : undefined, border: "none", borderRadius: 10, fontWeight: 600 }}
                                                    >
                                                        Enregistrer le règlement
                                                    </Button>
                                                    {erreurs > 0 && (
                                                        <div style={{ marginTop: 8, color: DANGER, fontSize: 13 }}>
                                                            <WarningFilled /> {erreurs} dossier(s) dépassent le SAP autorisé.
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default GestionPaiementSinistreEntitesConstatV2;
